import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { formatDuration, parseDurationToSeconds, formatSummary } from './transcriptionUtils';
import { buildCourtSummaryDocument } from './courtSummaryDoc';
import { downloadCourtSummaryPdf } from './courtSummaryPdf';
import { marked } from 'marked';
import axios from 'axios';
import { VERSION_CONTROL_API_URL } from '../../config/api';

// Configure marked for plain text rendering
marked.setOptions({
  breaks: true,
  gfm: true,
});

const exportFileName = (title, suffix, ext) => {
  const base = String(title || 'transcription')
    .replace(/[^a-z0-9]+/gi, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase() || 'transcription';
  return `${base}${suffix || ''}.${ext}`;
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const xmlEscape = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

const crc32 = (bytes) => {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    crc ^= bytes[i];
    for (let bit = 0; bit < 8; bit += 1) {
      crc = (crc >>> 1) ^ (crc & 1 ? 0xedb88320 : 0);
    }
  }
  return (crc ^ 0xffffffff) >>> 0;
};

const u16 = (value) => [value & 0xff, (value >>> 8) & 0xff];
const u32 = (value) => [value & 0xff, (value >>> 8) & 0xff, (value >>> 16) & 0xff, (value >>> 24) & 0xff];

const buildZip = (files) => {
  const encoder = new TextEncoder();
  const locals = [];
  const central = [];
  let offset = 0;

  files.forEach((file) => {
    const name = encoder.encode(file.name);
    const data = encoder.encode(file.content);
    const checksum = crc32(data);
    const local = [
      ...u32(0x04034b50),
      ...u16(20),
      ...u16(0),
      ...u16(0),
      ...u16(0),
      ...u16(0),
      ...u32(checksum),
      ...u32(data.length),
      ...u32(data.length),
      ...u16(name.length),
      ...u16(0),
      ...name,
      ...data,
    ];
    locals.push(local);
    central.push([
      ...u32(0x02014b50),
      ...u16(20),
      ...u16(20),
      ...u16(0),
      ...u16(0),
      ...u16(0),
      ...u16(0),
      ...u32(checksum),
      ...u32(data.length),
      ...u32(data.length),
      ...u16(name.length),
      ...u16(0),
      ...u16(0),
      ...u16(0),
      ...u16(0),
      ...u32(0),
      ...u32(offset),
      ...name,
    ]);
    offset += local.length;
  });

  const centralStart = offset;
  const chunks = [...locals, ...central, [[
    ...u32(0x06054b50),
    ...u16(0),
    ...u16(0),
    ...u16(files.length),
    ...u16(files.length),
    ...u32(central.flat().length),
    ...u32(centralStart),
    ...u16(0),
  ]]];
  const parts = chunks.map((chunk) => Uint8Array.from(chunk.flat()));
  const total = parts.reduce((sum, part) => sum + part.length, 0);
  const bytes = new Uint8Array(total);
  let cursor = 0;
  parts.forEach((part) => {
    bytes.set(part, cursor);
    cursor += part.length;
  });
  return new Blob([bytes], {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
};

const buildDocxBlob = (paragraphs) => {
  const body = paragraphs.map((paragraph) => {
    const heading = paragraph.heading;
    const runProps = heading ? '<w:rPr><w:b/><w:sz w:val="32"/></w:rPr>' : '';
    const text = xmlEscape(paragraph.text || '');
    return `<w:p><w:r>${runProps}<w:t xml:space="preserve">${text}</w:t></w:r></w:p>`;
  }).join('');

  const documentXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:body>${body}<w:sectPr><w:pgSz w:w="12240" w:h="15840"/><w:pgMar w:top="1440" w:right="1440" w:bottom="1440" w:left="1440"/></w:sectPr></w:body>
</w:document>`;

  return buildZip([
    {
      name: '[Content_Types].xml',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
</Types>`,
    },
    {
      name: '_rels/.rels',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
</Relationships>`,
    },
    { name: 'word/document.xml', content: documentXml },
  ]);
};

const textToParagraphs = (text) => String(text || '')
  .split(/\n/)
  .map((line) => ({ text: line }));

const downloadDocxText = (filename, blocks) => {
  downloadBlob(buildDocxBlob(blocks), filename);
};

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

const downloadTextAsPdf = async (filename, title, body) => {
  const host = document.createElement('div');
  host.style.cssText = [
    'position:fixed',
    'left:0',
    'top:0',
    'width:794px',
    'padding:48px',
    'background:#ffffff',
    'color:#111827',
    'font:16px/1.55 Arial, "Noto Sans SC", "PingFang SC", "Microsoft YaHei", sans-serif',
    'z-index:-1',
  ].join(';');
  host.innerHTML = `<h1 style="font-size:22px;margin:0 0 16px;">${escapeHtml(title)}</h1><div style="white-space:pre-wrap;">${escapeHtml(body || 'No content available')}</div>`;
  document.body.appendChild(host);
  try {
    const canvas = await html2canvas(host, { scale: 2, backgroundColor: '#ffffff' });
    const pdf = new jsPDF('p', 'pt', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const image = canvas.toDataURL('image/jpeg', 0.92);
    let heightLeft = imgHeight;
    let position = 0;
    pdf.addImage(image, 'JPEG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
    while (heightLeft > 8) {
      position -= pageHeight;
      pdf.addPage();
      pdf.addImage(image, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }
    pdf.save(filename);
  } finally {
    host.remove();
  }
};

// Helper function to preserve markdown formatting in plain text
const markdownToPlainText = (markdownContent) => {
  if (!markdownContent) return '';
  
  // Apply formatting fixes first
  let formattedText = markdownContent
    // Fix missing spaces after colons
    .replace(/:\s*([^\s])/g, ': $1')
    // Remove backslashes that appear after numbers in headings (e.g., "1\. CASE" -> "1. CASE")
    .replace(/(\d+)\\\s*\./g, '$1.')
    // Clean up multiple spaces (but keep newlines)
    .replace(/[^\S\n]+/g, ' ')
    // Clean up multiple newlines
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  
  return formattedText;
};

// Helper function to parse markdown and extract formatted segments for PDF
const parseMarkdownForPDF = (markdownContent) => {
  if (!markdownContent) return [];
  
  // This function will return an array of objects with text and formatting info
  const segments = [];
  let currentText = '';
  let bold = false;
  let italic = false;
  let i = 0;
  
  const flushCurrent = () => {
    if (currentText) {
      segments.push({ text: currentText, bold, italic });
      currentText = '';
    }
  };
  
  while (i < markdownContent.length) {
    const char = markdownContent[i];
    const next = markdownContent[i + 1];
    const afterNext = markdownContent[i + 2];
    
    // Check for bold and italic (***text***)
    if (char === '*' && next === '*' && afterNext === '*') {
      // Find the closing ***
      let j = i + 3;
      let found = false;
      while (j < markdownContent.length - 2) {
        if (markdownContent[j] === '*' && markdownContent[j + 1] === '*' && markdownContent[j + 2] === '*') {
          // Found closing ***
          const text = markdownContent.substring(i + 3, j);
          flushCurrent();
          segments.push({ text, bold: true, italic: true });
          i = j + 3;
          found = true;
          break;
        }
        j++;
      }
      if (!found) {
        currentText += char;
        i++;
      }
    }
    // Check for bold (**text**)
    else if (char === '*' && next === '*' && afterNext !== '*') {
      // Find the closing **
      let j = i + 2;
      let found = false;
      while (j < markdownContent.length - 1) {
        if (markdownContent[j] === '*' && markdownContent[j + 1] === '*' && markdownContent[j + 2] !== '*') {
          const text = markdownContent.substring(i + 2, j);
          flushCurrent();
          segments.push({ text, bold: true, italic: false });
          i = j + 2;
          found = true;
          break;
        }
        j++;
      }
      if (!found) {
        currentText += char;
        i++;
      }
    }
    // Check for italic (*text*)
    else if (char === '*' && next !== '*') {
      // Find the closing *
      let j = i + 1;
      let found = false;
      while (j < markdownContent.length) {
        if (markdownContent[j] === '*' && markdownContent[j + 1] !== '*') {
          const text = markdownContent.substring(i + 1, j);
          flushCurrent();
          segments.push({ text, bold: false, italic: true });
          i = j + 1;
          found = true;
          break;
        }
        j++;
      }
      if (!found) {
        currentText += char;
        i++;
      }
    } else {
      currentText += char;
      i++;
    }
  }
  
  flushCurrent();
  
  return segments;
};

// Helper function to convert Markdown to formatted text for downloads
const markdownToFormattedText = (markdownContent) => {
  if (!markdownContent) return '';
  
  
  // FIRST: Remove ALL percentage signs immediately - no exceptions
  let formattedText = markdownContent.replace(/%/g, '');
  
  // Process Markdown to preserve formatting and spacing
  formattedText = formattedText
    // Headers - convert to uppercase with proper spacing and lines
    .replace(/^#{1}\s*(.+)$/gm, '\n$1\n' + '─'.repeat(50) + '\n')
    .replace(/^#{2}\s*(.+)$/gm, '\n$1\n' + '─'.repeat(30) + '\n')
    .replace(/^#{3,6}\s*(.+)$/gm, '\n$1\n')
    
    // Handle headers that might have numbers or special characters - but be more specific
    .replace(/^#{1,2}\s*\d+\.\s*(.+)$/gm, '\n$1\n' + '─'.repeat(50) + '\n')
    
    // Bold text - keep bold but remove asterisks
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/__(.*?)__/g, '$1')
    
    // Italic text - keep italic but remove asterisks
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/_(.*?)_/g, '$1')
    
    // Code blocks - preserve with indentation
    .replace(/```[\s\S]*?```/g, (match) => {
      const code = match.replace(/```/g, '').trim();
      return '\n' + code.split('\n').map(line => '    ' + line).join('\n') + '\n';
    })
    
    // Inline code - keep as is but remove backticks
    .replace(/`([^`]+)`/g, '$1')
    
    // Links - keep text, remove URL
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    
    // Lists - convert to bullet points with proper spacing
    .replace(/^[\s]*[-*+]\s+(.+)$/gm, '• $1')
    .replace(/^[\s]*\d+\.\s+(.+)$/gm, '• $1')
    
    // Blockquotes - add indentation
    .replace(/^>\s*(.+)$/gm, '    $1')
    
    // Horizontal rules - convert to lines
    .replace(/^[-*_]{3,}$/gm, '\n' + '─'.repeat(50) + '\n')
    
    // Remove any remaining equals signs that might be used as separators
    .replace(/^=+$/gm, '─'.repeat(50))
    
    // SECOND: Remove ALL percent signs again - double check
    .replace(/%/g, '')
    
    // Multiple newlines - normalize to double newlines
    .replace(/\n{3,}/g, '\n\n')
    
    // Clean up extra whitespace
    .trim();
  
  // THIRD: Final percentage removal - nuclear option
  formattedText = formattedText.replace(/%/g, '');
  
  return formattedText;
};

// Alternative function to clean up any remaining formatting issues
const cleanFormattedText = (text) => {
  if (!text) return '';
  
  return text
    // Remove any remaining hashtags
    .replace(/#/g, '')
    // Remove any remaining asterisks
    .replace(/\*/g, '')
    // Remove any remaining underscores used for formatting
    .replace(/_/g, '')
    // Replace any remaining equals signs with lines
    .replace(/=+/g, '─'.repeat(50))
    // Remove ALL percent signs completely - no exceptions
    .replace(/%/g, '')
    // Fix missing spaces after colons (but not if already followed by a space)
    .replace(/:\s*([^\s])/g, ': $1')
    // Remove backslashes that appear after numbers in headings (e.g., "1\. CASE" -> "1. CASE")
    .replace(/(\d+)\\\s*\./g, '$1.')
    // Clean up multiple spaces
    .replace(/\s+/g, ' ')
    // Clean up multiple newlines
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

// Specialized function to aggressively remove percentage separators
const removePercentageSeparators = (text) => {
  if (!text) return '';
  
  
  let cleaned = text;
  
  // Step 1: Remove ALL percentage signs completely - no exceptions
  cleaned = cleaned.replace(/%/g, '');
  
  // Step 2: Find lines that were likely separators (empty or mostly empty lines)
  // and replace them with proper separator lines
  cleaned = cleaned
    // Replace empty lines with separator lines
    .replace(/^\s*$/gm, '─'.repeat(50))
    // Replace lines with only whitespace and dashes/underscores with separator lines
    .replace(/^[\s\-_]*$/gm, '─'.repeat(50))
    // Clean up multiple consecutive separator lines
    .replace(/(─{50}\s*\n\s*){2,}/g, '─'.repeat(50) + '\n');
  
  return cleaned.trim();
};

// Final cleanup function to ensure NO percentage signs remain anywhere
const finalPercentageCleanup = (text) => {
  if (!text) return '';
  
  
  let cleaned = text;
  
  // Nuclear option: Remove ALL percentage signs from the entire text
  cleaned = cleaned.replace(/%/g, '');
  
  // Add proper separators where needed
  cleaned = cleaned
    // Add separators after major headings
    .replace(/^(\d+\.\s+[A-Z][^\\n]*)$/gm, '$1\n' + '─'.repeat(50) + '\n')
    // Add separators after section headers
    .replace(/^([A-Z][A-Z\s&]+:?)$/gm, '$1\n' + '─'.repeat(30) + '\n')
    // Clean up multiple consecutive separators
    .replace(/(─{30,}\s*\n\s*){2,}/g, '─'.repeat(50) + '\n');
  
  return cleaned.trim();
};

// Debug function to trace percentage signs
const debugPercentageSigns = (text, stage) => {
  if (!text) return;
  const percentageCount = (text.match(/%/g) || []).length;
  if (percentageCount > 0) {
    // console.error(`🚨 PERCENTAGE SIGNS FOUND at ${stage}: ${percentageCount} occurrences`);
    // console.error('Content with percentage signs:', text);
    // Find and log lines with percentage signs
    const lines = text.split('\n');
    lines.forEach((line, index) => {
      if (line.includes('%')) {
        // console.error(`Line ${index + 1}: "${line}"`);
      }
    });
  } else {
  }
};

// Test function to process the exact content you provided
const testContentProcessing = (content) => {
  
  // Test each step
  const step1 = content.replace(/%/g, '');
  
  const step2 = markdownToFormattedText(content);
  // console.log('Step 2 - markdownToFormattedText:', step2);
  
  const step3 = removePercentageSeparators(step2);
  // console.log('Step 3 - removePercentageSeparators:', step3);
  
  const step4 = cleanFormattedText(step3);
  // console.log('Step 4 - cleanFormattedText:', step4);
  
  const step5 = finalPercentageCleanup(step4);
  // console.log('Step 5 - finalPercentageCleanup:', step5);
  
  return step5;
};

// Simple, safe content processor that just removes percentage signs and formats basic markdown
const simpleContentProcessor = (content) => {
  if (!content) {
    // console.log('🔧 SIMPLE CONTENT PROCESSOR - No content provided');
    return '';
  }
  
  // console.log('🔧 SIMPLE CONTENT PROCESSOR - Input length:', content.length);
  // console.log('🔧 SIMPLE CONTENT PROCESSOR - Input preview:', content.substring(0, 200) + '...');
  
  let processed = content;
  
  // Remove ALL percentage signs first
  processed = processed.replace(/%/g, '');
  
  // Basic markdown processing - preserve formatting
  processed = processed
    // Convert headers to plain text with proper spacing
    .replace(/^#{1}\s*(.+)$/gm, '\n$1\n' + '─'.repeat(50) + '\n')
    .replace(/^#{2}\s*(.+)$/gm, '\n$1\n' + '─'.repeat(30) + '\n')
    .replace(/^#{3,6}\s*(.+)$/gm, '\n$1\n')
    // Keep bold formatting - convert to uppercase for emphasis
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/__(.*?)__/g, '$1')
    // Keep italic formatting - convert to regular text
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/_(.*?)_/g, '$1')
    // Convert lists to bullet points
    .replace(/^[\s]*[-*+]\s+(.+)$/gm, '• $1')
    .replace(/^[\s]*\d+\.\s+(.+)$/gm, '• $1')
    // Clean up extra whitespace
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  
  // console.log('🔧 SIMPLE CONTENT PROCESSOR - Output length:', processed.length);
  // console.log('🔧 SIMPLE CONTENT PROCESSOR - Output preview:', processed.substring(0, 200) + '...');
  return processed;
};

// Enhanced content processor that preserves formatting for PDFs
const enhancedContentProcessor = (content) => {
  if (!content) {
    // console.log('🔧 ENHANCED CONTENT PROCESSOR - No content provided');
    return '';
  }
  
  // console.log('🔧 ENHANCED CONTENT PROCESSOR - Input length:', content.length);
  // console.log('🔧 ENHANCED CONTENT PROCESSOR - Input preview:', content.substring(0, 200) + '...');
  
  let processed = content;
  
  // Remove ALL percentage signs first
  processed = processed.replace(/%/g, '');
  
  // Enhanced markdown processing - preserve visual formatting
  processed = processed
    // Convert headers to formatted text with separators
    .replace(/^#{1}\s*(.+)$/gm, '\n$1\n' + '─'.repeat(50) + '\n')
    .replace(/^#{2}\s*(.+)$/gm, '\n$1\n' + '─'.repeat(30) + '\n')
    .replace(/^#{3,6}\s*(.+)$/gm, '\n$1\n')
    // Preserve bold formatting by converting to uppercase
    .replace(/\*\*(.*?)\*\*/g, (match, text) => text.toUpperCase())
    .replace(/__(.*?)__/g, (match, text) => text.toUpperCase())
    // Preserve italic formatting by adding emphasis markers
    .replace(/\*(.*?)\*/g, (match, text) => `[${text}]`)
    .replace(/_(.*?)_/g, (match, text) => `[${text}]`)
    // Convert lists to bullet points
    .replace(/^[\s]*[-*+]\s+(.+)$/gm, '• $1')
    .replace(/^[\s]*\d+\.\s+(.+)$/gm, '• $1')
    // Clean up extra whitespace
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  
  // console.log('🔧 ENHANCED CONTENT PROCESSOR - Output length:', processed.length);
  // console.log('🔧 ENHANCED CONTENT PROCESSOR - Output preview:', processed.substring(0, 200) + '...');
  return processed;
};

// Function to add formatted text to PDF with proper bold/italic styling
const addFormattedTextToPDF = (doc, text, x, y, maxWidth = 170) => {
  if (!text) return y;
  
  // Preprocess the entire text to remove hash tags and fix spacing
  let processedText = text;
  
  // Remove percentage signs first
  processedText = processedText.replace(/%/g, '');
  
  // Handle the specific pattern: "TEXT## NUMBER. TEXT" (like "COURT PROCEEDING SUMMARY - U.S. SUPREME COURT## 1. CASE HEADER INFORMATION")
  processedText = processedText.replace(/(.*?)(#{2,})\s*(\d+\.\s*.*?)(?=\n|$)/g, '$1\n$3');

  // Handle hash tags at the beginning of lines like "## HEADER"
  processedText = processedText.replace(/^#{1,6}\s*(.*?)(?=\n|$)/gm, '\n$1\n');
  
  // Handle any remaining hash tags anywhere in the text (more aggressive)
  processedText = processedText.replace(/#{1,6}\s*([^#\n]+)/g, (match, headerText) => {
    // Extract just the header text without hash tags
    return '\n' + headerText.trim() + '\n';
  });
  
  // Final cleanup - remove any remaining hash tags
  processedText = processedText.replace(/#{1,6}/g, '');
  
  // Convert list items (* item -> • item) but preserve formatting within them
  processedText = processedText.replace(/^\*\s+/gm, '• ');
  
  // Fix excessive spacing - replace multiple newlines with double newlines
  processedText = processedText.replace(/\n{3,}/g, '\n\n');
  
  // Add spaces after colons (but not if already followed by a space)
  processedText = processedText.replace(/:\s*([^\s])/g, ': $1');
  
  // Remove backslashes that appear after numbers in headings (e.g., "1\. CASE" -> "1. CASE")
  processedText = processedText.replace(/(\d+)\\\s*\./g, '$1.');
  
  // Remove ALL remaining backslashes (after specific handling above)
  processedText = processedText.replace(/\\/g, '');
  
  // Add spaces before opening parentheses (but not if already preceded by a space)
  // This handles cases like "text(" -> "text (" and "**bold**(" -> "**bold** ("
  processedText = processedText.replace(/([^\s\*])\s*\(/g, '$1 (');
  
  // Split text into lines and process each line
  const lines = processedText.split('\n');
  let currentY = y;
  
  lines.forEach(line => {
    if (currentY > 270) {
      doc.addPage();
      currentY = 20;
    }
    
    // Process the line for formatting
    const formattedSegments = parseFormattedText(line);
    
    let currentX = x;
    let lineHeight = 6;
    
    formattedSegments.forEach(segment => {
      // Check if this segment would exceed the line width
      const segmentWidth = doc.getTextWidth(segment.text);
      
      if (currentX + segmentWidth > x + maxWidth && currentX > x) {
        // Move to next line
        currentY += lineHeight;
        currentX = x;
        
        if (currentY > 270) {
          doc.addPage();
          currentY = 20;
        }
      }
      
      // Set font style based on segment type
      // console.log('🔍 Rendering segment:', segment.text, 'bold:', segment.bold, 'italic:', segment.italic);
      if (segment.bold && segment.italic) {
        // Both bold and italic - use bolditalic if available, otherwise bold
        doc.setFont('helvetica', 'bolditalic');
      } else if (segment.bold) {
        doc.setFont('helvetica', 'bold');
      } else if (segment.italic) {
        doc.setFont('helvetica', 'italic');
      } else {
        doc.setFont('helvetica', 'normal');
      }
      
      // If the segment is still too wide for the line, split it
      if (currentX + segmentWidth > x + maxWidth) {
        const words = segment.text.split(' ');
        let currentLine = '';
        
        words.forEach(word => {
          const testLine = currentLine + (currentLine ? ' ' : '') + word;
          const testWidth = doc.getTextWidth(testLine);
          
          if (testWidth > maxWidth && currentLine) {
            // Add current line and move to next
            doc.text(currentLine, currentX, currentY);
            currentY += lineHeight;
            currentX = x;
            currentLine = word;
            
            if (currentY > 270) {
              doc.addPage();
              currentY = 20;
            }
          } else {
            currentLine = testLine;
          }
        });
        
        // Add the last line
        if (currentLine) {
          doc.text(currentLine, currentX, currentY);
          currentX += doc.getTextWidth(currentLine);
        }
      } else {
        // Add the text normally
        doc.text(segment.text, currentX, currentY);
        currentX += segmentWidth;
      }
    });
    
    currentY += lineHeight;
  });
  
  return currentY;
};

// Function to parse text and identify bold/italic segments with proper markdown handling
const parseFormattedText = (text) => {
  if (!text) return [{ text: '', bold: false, italic: false }];
  
  let processedText = text;
  const segments = [];
  
  // Step 1: Find all formatting patterns and replace them with placeholders
  const placeholders = [];
  let placeholderIndex = 0;
  
  // CRITICAL: First handle patterns that start with asterisk (list items with bold/italic)
  // Handle "* ***text***" pattern (bullet + bold+italic)
  processedText = processedText.replace(/•\s+\*\*\*(.*?)\*\*\*/g, (match, content) => {
    const placeholder = `__LIST_BOLD_ITALIC_${placeholderIndex}__`;
    placeholders.push({ placeholder, content, type: 'bold_italic' });
    placeholderIndex++;
    return `• ${placeholder}`;
  });
  
  // Handle "* **text**" pattern (bullet + bold)
  processedText = processedText.replace(/•\s+\*\*(.*?)\*\*/g, (match, content) => {
    const placeholder = `__LIST_BOLD_${placeholderIndex}__`;
    placeholders.push({ placeholder, content, type: 'bold' });
    placeholderIndex++;
    return `• ${placeholder}`;
  });
  
  // Handle "* *text*" pattern (bullet + italic)
  processedText = processedText.replace(/•\s+\*(.*?)\*/g, (match, content) => {
    const placeholder = `__LIST_ITALIC_${placeholderIndex}__`;
    placeholders.push({ placeholder, content, type: 'italic' });
    placeholderIndex++;
    return `• ${placeholder}`;
  });
  
  // Now handle standalone formatting patterns (not at start of lines)
  // First handle triple asterisks (***text***) - these should be both bold AND italic
  processedText = processedText.replace(/\*\*\*(.*?)\*\*\*/g, (match, content) => {
    const placeholder = `__BOLD_ITALIC_${placeholderIndex}__`;
    placeholders.push({ placeholder, content, type: 'bold_italic' });
    placeholderIndex++;
    return placeholder;
  });
  
  // Then handle double asterisks (**text**) - simple regex since *** was already handled
  processedText = processedText.replace(/\*\*(.*?)\*\*/g, (match, content) => {
    const placeholder = `__BOLD_${placeholderIndex}__`;
    placeholders.push({ placeholder, content, type: 'bold' });
    placeholderIndex++;
    return placeholder;
  });
  
  // Finally handle single asterisks (*text*) for italic - but skip list items already processed
  processedText = processedText.replace(/\*(.+?)\*/g, (match, content) => {
    // Skip if this is at the start of a line with • (already handled)
    if (match.startsWith('• ') || match.startsWith('•')) {
      return match;
    }
    
    // Check if this content already contains placeholders
    const containsPlaceholder = placeholders.some(p => content.includes(p.placeholder));
    if (containsPlaceholder) {
      return content;
    }
    
    // Treat as italic
    const placeholder = `__ITALIC_${placeholderIndex}__`;
    placeholders.push({ placeholder, content, type: 'italic' });
    placeholderIndex++;
    return placeholder;
  });
  
  // console.log('🔍 Processed text after replacements:', processedText);
  
  // Step 2: Now split the text and build segments
  let currentIndex = 0;
  
  // Find all placeholder positions
  const placeholderPositions = [];
  placeholders.forEach(ph => {
    const index = processedText.indexOf(ph.placeholder);
    if (index !== -1) {
      placeholderPositions.push({
        index,
        placeholder: ph.placeholder,
        content: ph.content,
        type: ph.type
      });
    }
  });
  
  // Sort by position
  placeholderPositions.sort((a, b) => a.index - b.index);
  
  // Build segments
  placeholderPositions.forEach(pos => {
    // Check if there's text before placeholder
    const hasTextBefore = pos.index > currentIndex;
    const beforeText = hasTextBefore ? processedText.substring(currentIndex, pos.index) : '';
    
    // Add text before placeholder
    if (beforeText) {
      segments.push({ text: beforeText, bold: false, italic: false });
    }
    
    // Add the formatted text
    // console.log('🔍 Adding formatted segment:', pos.content, 'bold:', pos.type === 'bold', 'italic:', pos.type === 'italic', 'bold_italic:', pos.type === 'bold_italic');
    // Check if this is a list item placeholder (starts with LIST_)
    const isListItem = pos.type.startsWith('LIST_');
    const actualType = isListItem ? pos.type.replace('LIST_', '') : pos.type;
    
    // Add spacing after formatted text for better readability
    // If text ends with colon, add 2 spaces; otherwise add 6 spaces
    const endsWithColon = pos.content.trim().endsWith(':');
    const spacing = endsWithColon ? '  ' : '      ';
    
    // Determine if a newline should be added before the list item
    // A list item should always start on a new line, unless it's the very first segment
    const shouldAddNewline = isListItem && segments.length > 0;
    const textWithSpacing = shouldAddNewline
      ? '\n' + pos.content + spacing 
      : pos.content + spacing;
    
    segments.push({
      text: textWithSpacing,
      bold: actualType === 'bold' || actualType === 'bold_italic',
      italic: actualType === 'italic' || actualType === 'bold_italic'
    });
    
    currentIndex = pos.index + pos.placeholder.length;
  });
  
  // Add remaining text
  if (currentIndex < processedText.length) {
    const remainingText = processedText.substring(currentIndex);
    if (remainingText) {
      segments.push({ text: remainingText, bold: false, italic: false });
    }
  }
  
  // If no formatting found, return the whole text as one segment
  if (segments.length === 0) {
    segments.push({ text: processedText, bold: false, italic: false });
  }
  
  // console.log('🔍 parseFormattedText - Output segments:', segments);
  return segments;
};

// Helper function to get duration from transcription object
const getTranscriptionDuration = (transcription) => {
  // If transcription.duration is already a formatted string, try to extract the raw value
  if (transcription.duration && typeof transcription.duration === 'string' && transcription.duration !== 'Unknown') {
    // Try to parse the formatted duration back to seconds
    const match = transcription.duration.match(/(\d+)h\s*(\d+)m|(\d+)m|(\d+)s/);
    if (match) {
      if (match[1] && match[2]) {
        // Format: "1h 23m"
        return parseInt(match[1]) * 3600 + parseInt(match[2]) * 60;
      } else if (match[3]) {
        // Format: "45m"
        return parseInt(match[3]) * 60;
      } else if (match[4]) {
        // Format: "30s"
        return parseInt(match[4]);
      }
    }
  }
  
  // Look for raw duration in various fields
  const allDurationFields = {
    duration_seconds: transcription.duration_seconds,
    audio_duration: transcription.audio_duration,
    length: transcription.length,
    audio_length: transcription.audio_length,
    audio_length_seconds: transcription.audio_length_seconds,
    audio_length_ms: transcription.audio_length_ms,
    file_duration: transcription.file_duration,
    file_duration_seconds: transcription.file_duration_seconds,
    seconds: transcription.seconds,
    length_seconds: transcription.length_seconds,
    metadata_duration: transcription.metadata?.duration,
    metadata_duration_seconds: transcription.metadata?.duration_seconds
  };
  
  // Find the first valid duration value
  for (const [key, value] of Object.entries(allDurationFields)) {
    if (value !== null && value !== undefined && value !== 0 && value !== '' && value !== '0') {
      const parsed = parseDurationToSeconds(value);
      if (parsed > 0) {
        // console.log(`Found duration in field ${key}: ${value} -> ${parsed} seconds`);
        return parsed;
      }
    }
  }
  
  // console.log('No valid duration found in transcription object:', transcription);
  return 0;
};

// Build the summary body for downloads.
// The stored summary is a structured object (see extractSummaryFromRecord), so it is
// first rendered to the same markdown shown on screen via formatSummary, then run
// through enhancedContentProcessor exactly like the transcript export. This keeps the
// summary formatting identical to the transcript and avoids treating the object as text.
export const buildSummaryText = (summary, transcription, isEditingSummary, editedSummary) => {
  const summarySource = isEditingSummary
    ? editedSummary
    : (formatSummary(summary) ?? (typeof summary === 'string' ? summary : ''));

  const processed = enhancedContentProcessor(summarySource);

  if (!processed) {
    return 'No summary available for this transcription.';
  }
  return processed;
};

// Function to download summary as DOCX
export const downloadSummaryDocx = (summary, transcription, isEditingSummary, editedSummary) => {
  // Get properly formatted summary text
  const cleanSummaryText = buildSummaryText(summary, transcription, isEditingSummary, editedSummary);

  // Create a professionally formatted summary document with consistent styling
  const content = `COURT TRANSCRIPTION SUMMARY
${'═'.repeat(60)}

CASE INFORMATION
${'─'.repeat(20)}

• Case Title: ${transcription?.title || 'N/A'}
• Date: ${transcription?.date || 'N/A'}
• Duration: ${formatDuration(getTranscriptionDuration(transcription))}
• Status: ${transcription?.status || 'N/A'}


SUMMARY CONTENT
${'─'.repeat(18)}

${cleanSummaryText}


${'═'.repeat(60)}
Generated by Snwolley CourtScribe
Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}
${'═'.repeat(60)}`;

  downloadDocxText(exportFileName(transcription?.title, '_summary', 'docx'), [
    { heading: 1, text: 'Court transcription summary' },
    ...textToParagraphs(content),
  ]);
};

// Function to download summary as PDF.
// Styled as the 8-part Judicial Service of Ghana transcript template (logo header,
// case details / participants / generation details / summary / transcript / review
// log / certification / distribution). Falls back to the plain layout when the
// summary has no content to template.
export const downloadSummaryPdf = async (summary, transcription, isEditingSummary, editedSummary) => {
  const model = buildCourtSummaryDocument(summary, transcription, isEditingSummary, editedSummary);
  if (model) {
    await downloadCourtSummaryPdf(
      model,
      exportFileName(transcription?.title, '_summary', 'pdf'),
    );
    return;
  }
  const cleanSummaryText = buildSummaryText(summary, transcription, isEditingSummary, editedSummary);
  // Mirror the transcript PDF layout: case information header followed by the body.
  const body = [
    `Case title: ${transcription?.title || 'N/A'}`,
    `Date: ${transcription?.date || 'N/A'}`,
    `Duration: ${formatDuration(getTranscriptionDuration(transcription))}`,
    `Status: ${transcription?.status || 'N/A'}`,
    '',
    cleanSummaryText,
    '',
    'Generated by Snwolley CourtScribe',
    `Generated on: ${new Date().toLocaleString()}`,
  ].join('\n');
  await downloadTextAsPdf(
    exportFileName(transcription?.title, '_summary', 'pdf'),
    'Court transcription summary',
    body,
  );
  return;
  const doc = new jsPDF();

  // Header section with professional styling
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text('COURT TRANSCRIPTION SUMMARY', 20, 25);
  
  // Add decorative line
  doc.setLineWidth(0.5);
  doc.line(20, 30, 190, 30);

  // Case information section
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('CASE INFORMATION', 20, 45);
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  let yPos = 55;
  
  // Case details with bullet points
  doc.text('• Case Title: ' + (transcription?.title || 'N/A'), 25, yPos);
  yPos += 7;
  doc.text('• Date: ' + (transcription?.date || 'N/A'), 25, yPos);
  yPos += 7;
  doc.text('• Duration: ' + formatDuration(getTranscriptionDuration(transcription)), 25, yPos);
  yPos += 7;
  doc.text('• Status: ' + (transcription?.status || 'N/A'), 25, yPos);
  yPos += 15;

  // Summary content section
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('SUMMARY CONTENT', 20, yPos);
  yPos += 5;
  
  doc.setLineWidth(0.3);
  doc.line(20, yPos, 190, yPos);
  yPos += 10;

  // Add summary content with proper formatting
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');

  // Get the raw summary content for formatting
  const rawSummaryContent = isEditingSummary ? editedSummary : summary;
  
  // Use the formatted text function to handle bold/italic properly
  yPos = addFormattedTextToPDF(doc, rawSummaryContent || 'No summary available', 20, yPos, 170);

  // Footer
  if (yPos > 250) {
    doc.addPage();
    yPos = 20;
  }
  yPos += 15;
  doc.setLineWidth(0.3);
  doc.line(20, yPos, 190, yPos);
  yPos += 10;
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'italic');
  doc.text('Generated by Snwolley CourtScribe', 20, yPos);
  doc.text(`Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`, 20, yPos + 5);

  const fileName = `${(transcription?.title || 'transcription_summary').replace(/[^a-z0-9]/gi, '_').toLowerCase()}_summary.pdf`;
  doc.save(fileName);
};

// Function to download transcription as DOCX
export const downloadAsDocx = (editedContent, transcription) => {
  // console.log('🔍 downloadAsDocx - received editedContent:', editedContent);
  // console.log('🔍 downloadAsDocx - content type:', typeof editedContent);
  // console.log('🔍 downloadAsDocx - content length:', editedContent ? editedContent.length : 0);
  
  // Convert Markdown to properly formatted text using enhanced processor
  const cleanContent = enhancedContentProcessor(editedContent) || 'No content available';
  // console.log('🔍 downloadAsDocx - cleanContent after enhancedContentProcessor:', cleanContent);

  // Create a professionally formatted document with consistent styling
  const content = `COURT TRANSCRIPTION DOCUMENT
${'═'.repeat(60)}

CASE INFORMATION
${'─'.repeat(20)}

• Case Title: ${transcription.title || 'N/A'}
• Date: ${transcription.date || 'N/A'}
• Duration: ${formatDuration(getTranscriptionDuration(transcription))}
• Status: ${transcription.status || 'N/A'}
• Word Count: ${transcription.wordCount || 'N/A'}


TRANSCRIPTION CONTENT
${'─'.repeat(25)}

${cleanContent}


${'═'.repeat(60)}
Generated by Snwolley CourtScribe
Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}
${'═'.repeat(60)}`;

  downloadDocxText(exportFileName(transcription?.title, '', 'docx'), [
    { heading: 1, text: 'Court transcription' },
    ...textToParagraphs(content),
  ]);
};

// Function to download transcription as PDF
export const downloadAsPdf = async (editedContent, transcription) => {
  const cleanContent = enhancedContentProcessor(editedContent) || 'No content available';
  const body = [
    `Case title: ${transcription?.title || 'N/A'}`,
    `Date: ${transcription?.date || 'N/A'}`,
    `Duration: ${formatDuration(getTranscriptionDuration(transcription))}`,
    `Status: ${transcription?.status || 'N/A'}`,
    '',
    cleanContent,
    '',
    'Generated by Snwolley CourtScribe',
    `Generated on: ${new Date().toLocaleString()}`,
  ].join('\n');
  await downloadTextAsPdf(
    exportFileName(transcription?.title, '', 'pdf'),
    'Court transcription',
    body,
  );
  return;
  // console.log('🔍 downloadAsPdf - received editedContent:', editedContent);
  // console.log('🔍 downloadAsPdf - content type:', typeof editedContent);
  // console.log('🔍 downloadAsPdf - content length:', editedContent ? editedContent.length : 0);
  
  const doc = new jsPDF();
  
  // Header section with professional styling
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text('COURT TRANSCRIPTION DOCUMENT', 20, 25);
  
  // Add decorative line
  doc.setLineWidth(0.5);
  doc.line(20, 30, 190, 30);

  // Case information section
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('CASE INFORMATION', 20, 45);
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  let yPos = 55;
  
  // Case details with bullet points
  doc.text('• Case Title: ' + (transcription.title || 'N/A'), 25, yPos);
  yPos += 7;
  doc.text('• Date: ' + (transcription.date || 'N/A'), 25, yPos);
  yPos += 7;
  doc.text('• Duration: ' + formatDuration(getTranscriptionDuration(transcription)), 25, yPos);
  yPos += 7;
  doc.text('• Status: ' + (transcription.status || 'N/A'), 25, yPos);
  yPos += 7;
  doc.text('• Word Count: ' + (transcription.wordCount || 'N/A'), 25, yPos);
  yPos += 15;

  // Content section
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('TRANSCRIPTION CONTENT', 20, yPos);
  yPos += 5;
  
  doc.setLineWidth(0.3);
  doc.line(20, yPos, 190, yPos);
  yPos += 10;
  
  // Add transcription content with proper formatting
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  
  // Use the formatted text function to handle bold/italic properly
  yPos = addFormattedTextToPDF(doc, editedContent || 'No content available', 20, yPos, 170);

  // Footer
  if (yPos > 250) {
    doc.addPage();
    yPos = 20;
  }
  yPos += 15;
  doc.setLineWidth(0.3);
  doc.line(20, yPos, 190, yPos);
  yPos += 10;
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'italic');
  doc.text('Generated by Snwolley CourtScribe', 20, yPos);
  doc.text(`Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`, 20, yPos + 5);
  
  // Save the PDF
  doc.save(exportFileName(transcription?.title, '', 'pdf'));
};

// Function to download version as PDF
export const downloadVersionAsPDF = (version, transcription) => {
  const doc = new jsPDF();
  
  // Convert Markdown from DB version to plain text using marked library
  const cleanContent = markdownToPlainText(version.content || 'No content available');
  
  // Header section with professional styling
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text('COURT TRANSCRIPTION - VERSION ' + version.version_number, 20, 25);
  
  // Add decorative line
  doc.setLineWidth(0.5);
  doc.line(20, 30, 190, 30);

  // Version information section
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('VERSION INFORMATION', 20, 45);
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  let yPos = 55;
  
  // Version details with bullet points
  doc.text('• Created: ' + new Date(version.created_at).toLocaleString(), 25, yPos);
  yPos += 7;
  doc.text('• Created By: ' + (version.created_by_name || 'Unknown User'), 25, yPos);
  yPos += 7;
  doc.text('• Duration: ' + formatDuration(getTranscriptionDuration(transcription)), 25, yPos);
  yPos += 7;
  doc.text('• Word Count: ' + (version.word_count || 'N/A'), 25, yPos);
  yPos += 7;
  doc.text('• Character Count: ' + (version.character_count || 'N/A'), 25, yPos);
  yPos += 15;

  // Content section
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('TRANSCRIPTION CONTENT', 20, yPos);
  yPos += 5;
  
  doc.setLineWidth(0.3);
  doc.line(20, yPos, 190, yPos);
  yPos += 10;

  // Add transcription content with proper formatting
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');

  // Use the formatted text function to handle bold/italic properly from DB content
  yPos = addFormattedTextToPDF(doc, version.content || 'No content available', 20, yPos, 170);

  // Footer
  if (yPos > 250) {
    doc.addPage();
    yPos = 20;
  }
  yPos += 15;
  doc.setLineWidth(0.3);
  doc.line(20, yPos, 190, yPos);
  yPos += 10;
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'italic');
  doc.text('Generated by Snwolley CourtScribe', 20, yPos);
  doc.text(`Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`, 20, yPos + 5);
  
  // Save the PDF
  const fileName = `${transcription.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_v${version.version_number}.pdf`;
  doc.save(fileName);
};

// Function to download version as DOCX
// Helper function to convert markdown to clean text for DOCX (applies all PDF preprocessing rules)
const markdownToDocxText = (markdownContent) => {
  if (!markdownContent) return '';
  
  // Apply the same preprocessing rules as addFormattedTextToPDF
  let processedText = markdownContent;
  
  // Remove percentage signs first
  processedText = processedText.replace(/%/g, '');
  
  // Handle the specific pattern: "TEXT## NUMBER. TEXT"
  processedText = processedText.replace(/(.*?)(#{2,})\s*(\d+\.\s*.*?)(?=\n|$)/g, '$1\n$3');
  
  // Handle hash tags at the beginning of lines like "## HEADER"
  processedText = processedText.replace(/^#{1,6}\s*(.*?)(?=\n|$)/gm, '\n$1\n');
  
  // Handle any remaining hash tags anywhere in the text
  processedText = processedText.replace(/#{1,6}\s*([^#\n]+)/g, (match, headerText) => {
    return '\n' + headerText.trim() + '\n';
  });
  
  // Final cleanup - remove any remaining hash tags
  processedText = processedText.replace(/#{1,6}/g, '');
  
  // Convert list items (* item -> • item) but preserve formatting within them
  processedText = processedText.replace(/^\*\s+/gm, '• ');
  
  // Fix excessive spacing - replace multiple newlines with double newlines
  processedText = processedText.replace(/\n{3,}/g, '\n\n');
  
  // Add spaces after colons (but not if already followed by a space)
  processedText = processedText.replace(/:\s*([^\s])/g, ': $1');
  
  // Remove backslashes that appear after numbers in headings (e.g., "1\. CASE" -> "1. CASE")
  processedText = processedText.replace(/(\d+)\\\s*\./g, '$1.');
  
  // Remove ALL remaining backslashes
  processedText = processedText.replace(/\\/g, '');
  
  // Add spaces before opening parentheses (but not if already preceded by a space)
  processedText = processedText.replace(/([^\s\*])\s*\(/g, '$1 (');
  
  // Now process markdown formatting - strip asterisks for clean text
  // Process text through parseFormattedText to get segments
  const segments = parseFormattedText(processedText);
  
  // Convert segments to clean text, preserving spacing and formatting but removing asterisks
  let cleanText = '';
  segments.forEach(segment => {
    cleanText += segment.text;
  });
  
  return cleanText;
};

export const downloadVersionAsDOCX = (version, transcription) => {
  // Debug: Log the version content to verify edits are present
  console.log('🔍 Download Version DOCX - version.content length:', version.content?.length);
  console.log('🔍 Download Version DOCX - version.content preview:', version.content?.substring(0, 200));
  
  // Convert Markdown from DB version to text with formatting markers for DOCX
  const cleanContent = markdownToDocxText(version.content || 'No content available');

  // Create a professionally formatted document
  const content = `COURT TRANSCRIPTION DOCUMENT - VERSION ${version.version_number}
${'═'.repeat(60)}

VERSION INFORMATION
${'─'.repeat(25)}

• Case Title: ${transcription.title}
• Version: ${version.version_number}
• Created: ${new Date(version.created_at).toLocaleString()}
• Created By: ${version.created_by_name || 'Unknown User'}
• Word Count: ${version.word_count}
• Character Count: ${version.character_count}
• Change Type: ${version.change_type}
${version.change_summary ? `• Change Summary: ${version.change_summary}` : ''}


TRANSCRIPTION CONTENT
${'─'.repeat(25)}

${cleanContent}


${'═'.repeat(60)}
Generated by Snwolley CourtScribe
Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}
${'═'.repeat(60)}`;

  downloadDocxText(
    exportFileName(transcription?.title, `_v${version.version_number}`, 'docx'),
    [
      { heading: 1, text: `Court transcription — version ${version.version_number}` },
      ...textToParagraphs(content),
    ],
  );
};

// Function to download summary version as PDF
export const downloadSummaryVersionAsPDF = (version, transcription) => {
  const doc = new jsPDF();
  
  // Convert Markdown from DB version to plain text using marked library

  // Header section with professional styling
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text('COURT TRANSCRIPTION SUMMARY - VERSION ' + version.version_number, 20, 25);
  
  // Add decorative line
  doc.setLineWidth(0.5);
  doc.line(20, 30, 190, 30);

  // Version information section
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('VERSION INFORMATION', 20, 45);
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  let yPos = 55;
  
  // Version details with bullet points
  doc.text('• Created: ' + new Date(version.created_at).toLocaleString(), 25, yPos);
  yPos += 7;
  doc.text('• Created By: ' + (version.created_by_name || 'Unknown User'), 25, yPos);
  yPos += 7;
  doc.text('• Duration: ' + formatDuration(getTranscriptionDuration(transcription)), 25, yPos);
  yPos += 7;
  doc.text('• Word Count: ' + (version.word_count || 'N/A'), 25, yPos);
  yPos += 7;
  doc.text('• Character Count: ' + (version.character_count || 'N/A'), 25, yPos);
  yPos += 15;

  // Summary content section
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('SUMMARY CONTENT', 20, yPos);
  yPos += 5;
  
  doc.setLineWidth(0.3);
  doc.line(20, yPos, 190, yPos);
  yPos += 10;

  // Add summary content with proper formatting
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');

  // Use the formatted text function to handle bold/italic properly from DB content
  yPos = addFormattedTextToPDF(doc, version.content || 'No content available', 20, yPos, 170);

  // Footer
  if (yPos > 250) {
    doc.addPage();
    yPos = 20;
  }
  yPos += 15;
  doc.setLineWidth(0.3);
  doc.line(20, yPos, 190, yPos);
  yPos += 10;
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'italic');
  doc.text('Generated by Snwolley CourtScribe', 20, yPos);
  doc.text(`Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`, 20, yPos + 5);

  // Save the PDF
  const fileName = `${transcription.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_summary_v${version.version_number}.pdf`;
  doc.save(fileName);
};

// Function to process content for DOCX format (removes asterisks, preserves formatting)
const processContentForDOCX = (content) => {
  if (!content) return '';
  
  // Remove percentage signs first
  let processed = content.replace(/%/g, '');
  
  // Handle the specific pattern: "TEXT## NUMBER. TEXT" (like "COURT PROCEEDING SUMMARY - U.S. SUPREME COURT## 1. CASE HEADER INFORMATION")
  processed = processed.replace(/(.*?)(#{2,})\s*(\d+\.\s*.*?)(?=\n|$)/g, '$1\n$3');

  // Handle hash tags at the beginning of lines like "## HEADER"
  processed = processed.replace(/^#{1,6}\s*(.*?)(?=\n|$)/gm, '\n$1\n');
  
  // Handle any remaining hash tags anywhere in the text (more aggressive)
  processed = processed.replace(/#{1,6}\s*([^#\n]+)/g, (match, headerText) => {
    // Extract just the header text without hash tags
    return '\n' + headerText.trim() + '\n';
  });
  
  // Final cleanup - remove any remaining hash tags
  processed = processed.replace(/#{1,6}/g, '');
  
  // Convert list items (* item -> • item)
  processed = processed.replace(/^\*\s+/gm, '• ');
  
  // Process triple asterisks (***text***) - convert to bold+italic representation
  processed = processed.replace(/\*\*\*(.*?)\*\*\*/g, '***$1***');
  
  // Process double asterisks (**text**) - convert to bold representation  
  processed = processed.replace(/\*\*(.*?)\*\*/g, '**$1**');
  
  // Process single asterisks (*text*) - convert to italic representation
  processed = processed.replace(/\*(.*?)\*/g, '*$1*');
  
  // Add spaces after colons (but not if already followed by a space)
  processed = processed.replace(/:\s*([^\s])/g, ': $1');
  
  // Remove backslashes that appear after numbers in headings (e.g., "1\. CASE" -> "1. CASE")
  processed = processed.replace(/(\d+)\\\s*\./g, '$1.');
  
  // Add spaces before opening parentheses (but not if already preceded by a space)
  // This handles cases like "text(" -> "text (" and "**bold**(" -> "**bold** ("
  processed = processed.replace(/([^\s\*])\s*\(/g, '$1 (');
  
  // Clean up any remaining formatting issues
  processed = processed
    .replace(/\n{3,}/g, '\n\n')
    .trim();
    
  return processed;
};

// Function to download summary version as DOCX
export const downloadSummaryVersionAsDOCX = (version, transcription) => {
  // Debug: Log the version content to verify edits are present
  console.log('🔍 Download Summary Version DOCX - version.content length:', version.content?.length);
  console.log('🔍 Download Summary Version DOCX - version.content preview:', version.content?.substring(0, 200));
  
  // Convert Markdown from DB version to text with formatting markers for DOCX
  const cleanContent = markdownToDocxText(version.content || 'No content available');

  // Create a professionally formatted summary document
  const content = `COURT TRANSCRIPTION SUMMARY - VERSION ${version.version_number}
${'═'.repeat(60)}

VERSION INFORMATION
${'─'.repeat(25)}

• Case Title: ${transcription.title}
• Summary Version: ${version.version_number}
• Created: ${new Date(version.created_at).toLocaleString()}
• Created By: ${version.created_by_name || 'Unknown User'}
• Duration: ${formatDuration(getTranscriptionDuration(transcription))}
• Word Count: ${version.word_count}
• Character Count: ${version.character_count}
• Change Type: ${version.change_type}
${version.change_summary ? `• Change Summary: ${version.change_summary}` : ''}


SUMMARY CONTENT
${'─'.repeat(18)}

${cleanContent}


${'═'.repeat(60)}
Generated by Snwolley CourtScribe
Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}
${'═'.repeat(60)}`;

  downloadDocxText(
    exportFileName(transcription?.title, `_summary_v${version.version_number}`, 'docx'),
    [
      { heading: 1, text: `Court transcription summary — version ${version.version_number}` },
      ...textToParagraphs(content),
    ],
  );
};

// ========================================
// PANDOC-BASED DOWNLOAD FUNCTIONS
// These functions use the backend /api/v1/convert route to convert markdown
// to properly formatted DOCX/PDF files using pandoc
// ========================================

/**
 * Download transcription as DOCX using pandoc (via backend)
 */
export const downloadAsDocxWithPandoc = async (editedContent, transcription) => {
  try {
    console.log('📄 Downloading with pandoc (DOCX)...');
    
    const response = await axios.post(
      `${VERSION_CONTROL_API_URL}/convert`,
      {
        content: editedContent,
        format: 'docx',
        title: transcription?.title || 'transcription'
      },
      {
        responseType: 'blob',
        timeout: 60000, // 60 seconds
      }
    );
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${transcription?.title || 'transcription'}.docx`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    console.log('✅ Download completed');
  } catch (error) {
    console.error('❌ Download failed:', error);
    throw error;
  }
};

/**
 * Download transcription as PDF using pandoc (via backend)
 */
export const downloadAsPdfWithPandoc = async (editedContent, transcription) => {
  try {
    console.log('📄 Downloading with pandoc (PDF)...');
    
    const response = await axios.post(
      `${VERSION_CONTROL_API_URL}/convert`,
      {
        content: editedContent,
        format: 'pdf',
        title: transcription?.title || 'transcription'
      },
      {
        responseType: 'blob',
        timeout: 60000, // 60 seconds
      }
    );
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${transcription?.title || 'transcription'}.pdf`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    console.log('✅ Download completed');
  } catch (error) {
    console.error('❌ Download failed:', error);
    throw error;
  }
};

/**
 * Download summary as DOCX using pandoc (via backend)
 */
export const downloadSummaryDocxWithPandoc = async (summary, transcription, isEditingSummary, editedSummary) => {
  try {
    console.log('📄 Downloading summary with pandoc (DOCX)...');
    
    const summaryContent = isEditingSummary ? editedSummary : summary;
    
    const response = await axios.post(
      `${VERSION_CONTROL_API_URL}/convert`,
      {
        content: summaryContent,
        format: 'docx',
        title: `${transcription?.title || 'transcription'}_summary`
      },
      {
        responseType: 'blob',
        timeout: 60000, // 60 seconds
      }
    );
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${transcription?.title || 'summary'}_summary.docx`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    console.log('✅ Download completed');
  } catch (error) {
    console.error('❌ Download failed:', error);
    throw error;
  }
};

/**
 * Download summary as PDF using pandoc (via backend)
 */
export const downloadSummaryPdfWithPandoc = async (summary, transcription, isEditingSummary, editedSummary) => {
  try {
    console.log('📄 Downloading summary with pandoc (PDF)...');
    
    const summaryContent = isEditingSummary ? editedSummary : summary;
    
    const response = await axios.post(
      `${VERSION_CONTROL_API_URL}/convert`,
      {
        content: summaryContent,
        format: 'pdf',
        title: `${transcription?.title || 'transcription'}_summary`
      },
      {
        responseType: 'blob',
        timeout: 60000, // 60 seconds
      }
    );
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${transcription?.title || 'summary'}_summary.pdf`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    console.log('✅ Download completed');
  } catch (error) {
    console.error('❌ Download failed:', error);
    throw error;
  }
};

/**
 * Download version as DOCX using pandoc (via backend)
 */
export const downloadVersionAsDocxWithPandoc = async (version, transcription) => {
  try {
    console.log('📄 Downloading version with pandoc (DOCX)...');
    
    const response = await axios.post(
      `${VERSION_CONTROL_API_URL}/convert`,
      {
        content: version.content,
        format: 'docx',
        title: `${transcription?.title || 'transcription'}_v${version.version_number}`
      },
      {
        responseType: 'blob',
        timeout: 60000,
      }
    );
    
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${transcription?.title || 'transcription'}_v${version.version_number}.docx`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    console.log('✅ Version download completed');
  } catch (error) {
    console.error('❌ Version download failed:', error);
    throw error;
  }
};

/**
 * Download version as PDF using pandoc (via backend)
 */
export const downloadVersionAsPdfWithPandoc = async (version, transcription) => {
  try {
    console.log('📄 Downloading version with pandoc (PDF)...');
    
    const response = await axios.post(
      `${VERSION_CONTROL_API_URL}/convert`,
      {
        content: version.content,
        format: 'pdf',
        title: `${transcription?.title || 'transcription'}_v${version.version_number}`
      },
      {
        responseType: 'blob',
        timeout: 60000,
      }
    );
    
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${transcription?.title || 'transcription'}_v${version.version_number}.pdf`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    console.log('✅ Version download completed');
  } catch (error) {
    console.error('❌ Version download failed:', error);
    throw error;
  }
};

/**
 * Download summary version as DOCX using pandoc (via backend)
 */
export const downloadSummaryVersionAsDocxWithPandoc = async (version, transcription) => {
  try {
    console.log('📄 Downloading summary version with pandoc (DOCX)...');
    
    const response = await axios.post(
      `${VERSION_CONTROL_API_URL}/convert`,
      {
        content: version.content,
        format: 'docx',
        title: `${transcription?.title || 'transcription'}_summary_v${version.version_number}`
      },
      {
        responseType: 'blob',
        timeout: 60000,
      }
    );
    
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${transcription?.title || 'summary'}_summary_v${version.version_number}.docx`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    console.log('✅ Summary version download completed');
  } catch (error) {
    console.error('❌ Summary version download failed:', error);
    throw error;
  }
};

/**
 * Download summary version as PDF using pandoc (via backend)
 */
export const downloadSummaryVersionAsPdfWithPandoc = async (version, transcription) => {
  try {
    console.log('📄 Downloading summary version with pandoc (PDF)...');
    
    const response = await axios.post(
      `${VERSION_CONTROL_API_URL}/convert`,
      {
        content: version.content,
        format: 'pdf',
        title: `${transcription?.title || 'transcription'}_summary_v${version.version_number}`
      },
      {
        responseType: 'blob',
        timeout: 60000,
      }
    );
    
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${transcription?.title || 'summary'}_summary_v${version.version_number}.pdf`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    console.log('✅ Summary version download completed');
  } catch (error) {
    console.error('❌ Summary version download failed:', error);
    throw error;
  }
};
